import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getMondayISO, shiftWeek, formatWeekRange, formatInt, formatKg } from "@/lib/menu-utils";
import { Building2, Beef, UtensilsCrossed, Scale, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [weekStart, setWeekStart] = useState(getMondayISO());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary", { params: { week_start: weekStart } });
      setSummary(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const kpis = useMemo(() => [
    { label: "Empresas ativas", value: formatInt(summary?.companies_count ?? 0), sub: "cadastradas", icon: Building2 },
    { label: "Refeições/dia", value: formatInt(summary?.total_meals_per_day ?? 0), sub: "somando todas as empresas", icon: UtensilsCrossed },
    { label: "Proteínas", value: formatInt(summary?.proteins_count ?? 0), sub: `${formatInt(summary?.preparations_count ?? 0)} preparações`, icon: Beef },
    { label: "Kg — semana", value: formatKg(summary?.total_kg_week ?? 0), sub: `${formatInt(summary?.menus_configured ?? 0)} cardápios configurados`, icon: Scale },
  ], [summary]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Visão consolidada da operação semanal."
        actions={
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1">
            <Button variant="ghost" size="icon" data-testid="week-prev-btn" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} className="h-8 w-8"><ChevronLeft size={16} /></Button>
            <Input
              type="date" value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              data-testid="week-date-input" className="h-8 w-40 border-0 focus-visible:ring-0"
            />
            <Button variant="ghost" size="icon" data-testid="week-next-btn" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} className="h-8 w-8"><ChevronRight size={16} /></Button>
          </div>
        }
      />

      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3" data-testid="week-range-label">
        Semana de referência: <span className="text-slate-700 font-medium">{formatWeekRange(weekStart)}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="mp-kpi" data-testid={`kpi-${k.label}`}>
              <div className="flex items-start justify-between">
                <div className="mp-kpi-label">{k.label}</div>
                <Icon size={16} className="text-slate-400" />
              </div>
              <div className="mp-kpi-value">{loading ? "—" : k.value}</div>
              <div className="mp-kpi-sub">{k.sub}</div>
            </div>
          );
        })}
      </div>

      <Section title="Próximos passos">
        <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
          {[
            { to: "/empresas", title: "1. Cadastre as empresas", desc: "Nome e refeições/dia por empresa." },
            { to: "/proteinas", title: "2. Cadastre proteínas e preparações", desc: "Ex.: Frango grelhado 120g, Frango à parmegiana 180g." },
            { to: "/cardapios", title: "3. Monte o cardápio da semana", desc: "2 opções por dia, cálculo automático de kg." },
          ].map((s) => (
            <Link key={s.to} to={s.to} data-testid={`step-${s.to}`} className="p-5 hover:bg-slate-50 transition-colors">
              <div className="text-xs text-emerald-800 uppercase tracking-wide font-semibold">Passo</div>
              <div className="text-slate-900 font-medium mt-1">{s.title}</div>
              <div className="text-sm text-slate-500 mt-1">{s.desc}</div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}
